import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { useIsFocused } from '@react-navigation/native';

import { ErrorIcon } from '@/assets/icons/error';
import { Button } from '@/components/ButtonComponent';
import { ContactSupport } from '@/components/ContactSupport';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { failedKycCountSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useUserStore } from '@/hooks/use-user-store';
import { KycStackScreenProps } from '@/navigation/kyc/types';
import { exitKycToLogin } from '@/navigation/navigation';
import { common } from '@/styles/styles';

import { useKycFieldsStore } from '../../hooks/use-kyc-fields';
import { useKycRetryControl } from '../../hooks/use-kyc-retry-control';

const styles = StyleSheet.create({
    headerStyle: {
        paddingTop: 10,
        paddingBottom: 20,
        height: 'auto',
    },
    marginBottomContainer: {
        marginBottom: 40,
    },
    statusInfoContainer: {
        paddingHorizontal: 20,
    },
});

type ScreenProps = KycStackScreenProps<'KycVerificationFailed'>;

export const VerificationFailed = ({ navigation, route }: ScreenProps) => {
    const { t } = useTranslation(['kyc', 'common']);
    const actions = useUserStore(state => state.actions);

    const { verificationFailureMessage, ctaLabel, retryOrLogout } = useKycRetryControl({
        hasIdScanFailed: route?.params?.hasIdScanFailed,
        liveAccountEmail: route?.params?.liveAccountEmail,
    });
    const isFocused = useIsFocused();
    const { data: retryCount = 0 } = useAuthUserConfig({
        select: failedKycCountSelector,
        enabled: isFocused,
    });

    usePreventAndroidBackNavigation();

    const reset = useKycFieldsStore(state => state.reset);

    const onPressCta = () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.ATTEMPT_FAILED_RETRY, { attemptCount: retryCount });
        retryOrLogout();
    };
    const onClose = () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.ATTEMPT_FAILED_ABORT, { attemptCount: retryCount });
        reset();
        actions.logout();
        exitKycToLogin(navigation);
    };

    const handlePress = () => {
        onPressCta();
    };

    return (
        <Screen>
            <ScreenNavBar onClose={onClose} closeIconMode={'close'} />
            <View style={[common.flex, common.justifySpaceBetween]}>
                <View style={[common.flex, common.justifyCenter, common.alignCenter, styles.statusInfoContainer]}>
                    <ErrorIcon />
                    <Text
                        variant="headlineMedium"
                        fontWeight="bold"
                        lineHeight={32}
                        mt="s16"
                        testID="failedVerificationHeader"
                    >
                        {t('verification_failed_header')}
                    </Text>
                    <SizedBox value={12} />
                    <Text
                        variant="titleMedium"
                        fontWeight="400"
                        color={ds.colorToken.text.secondary}
                        textAlign="center"
                        lineHeight={24}
                        testID="failedVerificationMessage"
                    >
                        {verificationFailureMessage}
                    </Text>
                </View>

                <View style={[styles.marginBottomContainer, common.paddingHorizontal]}>
                    <Box mb="s16">
                        <Button
                            hierarchy={'primary'}
                            label={ctaLabel === 'login' ? t('common:login') : t(ctaLabel, { ns: 'common' })}
                            onPress={handlePress}
                            testID="retryButton"
                        />
                    </Box>
                    <ContactSupport />
                </View>
            </View>
        </Screen>
    );
};
